/**
 * OTP Encryption Utilities
 * provide OTP encryption, decryption and verification functionality using Web Crypto API
 */

import { OTP_EXPIRY_TIME } from '@common'
import { ErrorTrackingAPI } from '@services'

// encryption key material (in actual application, should use a more secure way)
// AES requires 128 or 256 bit keys, so we need 16 or 32 bytes
const ENCRYPTION_KEY_MATERIAL = 'FSlzNwL3p4uirTJFrMKRbVVI1mkWO6rMDI8NnjCYX/I='

/**
 * generate encryption key from key material
 * @param {string} keyMaterial - key material string
 * @returns {Promise<CryptoKey>} encryption key
 */
const generateKey = async keyMaterial => {
    const encoder = new TextEncoder()
    const keyData = encoder.encode(keyMaterial)

    // ensure key is exactly 32 bytes (256 bits) for AES-256-GCM
    let finalKeyData = keyData
    if (keyData.length < 32) {
        // pad with zeros if too short
        finalKeyData = new Uint8Array(32)
        finalKeyData.set(keyData)
    } else if (keyData.length > 32) {
        // truncate if too long
        finalKeyData = keyData.slice(0, 32)
    }

    return await crypto.subtle.importKey('raw', finalKeyData, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt'])
}

/**
 * encrypt text using AES-GCM
 * @param {string} text - text to encrypt
 * @param {CryptoKey} key - encryption key
 * @returns {Promise<string>} encrypted text (base64 encoded)
 */
const aesEncrypt = async (text, key) => {
    try {
        const encoder = new TextEncoder()
        const data = encoder.encode(text)

        // generate random IV (initialization vector)
        const iv = crypto.getRandomValues(new Uint8Array(12))

        // encrypt data
        const encrypted = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: iv }, key, data)

        // combine IV and encrypted data
        const combined = new Uint8Array(iv.length + encrypted.byteLength)
        combined.set(iv)
        combined.set(new Uint8Array(encrypted), iv.length)

        // convert to base64
        return btoa(String.fromCharCode(...combined))
    } catch (error) {
        ErrorTrackingAPI.sendError(error, 'OTPEncryption', 'aesEncrypt')
        return null
    }
}

/**
 * decrypt text using AES-GCM
 * @param {string} encryptedText - encrypted text (base64 encoded)
 * @param {CryptoKey} key - decryption key
 * @returns {Promise<string|null>} decrypted text
 */
const aesDecrypt = async (encryptedText, key) => {
    try {
        // decode base64
        const combined = new Uint8Array(
            atob(encryptedText)
                .split('')
                .map(char => char.charCodeAt(0))
        )

        // extract IV and encrypted data
        const iv = combined.slice(0, 12)
        const encrypted = combined.slice(12)

        // decrypt data
        const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: iv }, key, encrypted)

        // convert to string
        const decoder = new TextDecoder()
        return decoder.decode(decrypted)
    } catch (error) {
        ErrorTrackingAPI.sendError(error, 'OTPEncryption', 'aesDecrypt')
        return null
    }
}

/**
 * encrypt OTP
 * @param {string} otp - original OTP
 * @returns {Promise<string|null>} encrypted OTP
 */
export const encryptOTP = async otp => {
    if (!otp) return null

    try {
        // generate encryption key
        const key = await generateKey(ENCRYPTION_KEY_MATERIAL)

        // add timestamp and random number for security
        const timestamp = Date.now()
        const randomSalt = Math.random().toString(36).substring(2, 8)
        const dataToEncrypt = `${otp}_${timestamp}_${randomSalt}`

        return await aesEncrypt(dataToEncrypt, key)
    } catch (error) {
        ErrorTrackingAPI.sendError(error, 'OTPEncryption', 'encryptOTP')
        return null
    }
}

/**
 * decrypt OTP
 * @param {string} encryptedOTP - encrypted OTP
 * @returns {Promise<string|null>} decrypted OTP, return null if failed
 */
export const decryptOTP = async encryptedOTP => {
    if (!encryptedOTP) return null

    try {
        // generate decryption key
        const key = await generateKey(ENCRYPTION_KEY_MATERIAL)

        const decrypted = await aesDecrypt(encryptedOTP, key)
        if (!decrypted) return null

        // extract OTP (remove timestamp and random number)
        const parts = decrypted.split('_')
        return parts[0] // return original OTP
    } catch (error) {
        ErrorTrackingAPI.sendError(error, 'OTPEncryption', 'decryptOTP')
        return null
    }
}

/**
 * verify user input OTP matches encrypted OTP
 * @param {string} userInput - user input OTP
 * @param {string} encryptedOTP - encrypted OTP
 * @returns {Promise<boolean>} true if matches, false otherwise
 */
export const verifyOTP = async (userInput, encryptedOTP) => {
    if (!userInput || !encryptedOTP) return false

    const decryptedOTP = await decryptOTP(encryptedOTP)
    if (!decryptedOTP) return false

    return userInput === decryptedOTP
}

/**
 * check if encrypted OTP is expired (optional feature)
 * @param {string} encryptedOTP - encrypted OTP
 * @param {number} expiryMinutes - expiry time (minutes)
 * @returns {Promise<boolean>} true if expired, false otherwise
 */
export const isOTPExpired = async (encryptedOTP, expirySeconds = OTP_EXPIRY_TIME) => {
    if (!encryptedOTP) return true

    try {
        // generate decryption key
        const key = await generateKey(ENCRYPTION_KEY_MATERIAL)

        const decrypted = await aesDecrypt(encryptedOTP, key)
        if (!decrypted) return true

        const parts = decrypted.split('_')
        if (parts.length < 2) return true

        const timestamp = parseInt(parts[1])
        const now = Date.now()
        const expiryTime = expirySeconds * 1000 // convert seconds to milliseconds

        return now - timestamp > expiryTime
    } catch (error) {
        ErrorTrackingAPI.sendError(error, 'OTPEncryption', 'isOTPExpired')
        return true
    }
}

/**
 * clear encrypted OTP (optional feature)
 * @param {string} encryptedOTP - encrypted OTP to clear
 */
export const clearEncryptedOTP = encryptedOTP => {
    // in actual application, here can add additional security clearance logic
    return null
}

/**
 * encrypt password for temporary storage
 * @param {string} password - password to encrypt
 * @returns {Promise<string|null>} encrypted password
 */
export const encryptPassword = async password => {
    if (!password) return null

    try {
        // generate encryption key
        const key = await generateKey(ENCRYPTION_KEY_MATERIAL)

        // add timestamp for security
        const timestamp = Date.now()
        const dataToEncrypt = `${password}_${timestamp}`

        return await aesEncrypt(dataToEncrypt, key)
    } catch (error) {
        ErrorTrackingAPI.sendError(error, 'OTPEncryption', 'encryptPassword')
        return null
    }
}

/**
 * decrypt password
 * @param {string} encryptedPassword - encrypted password
 * @returns {Promise<string|null>} decrypted password
 */
export const decryptPassword = async encryptedPassword => {
    if (!encryptedPassword) return null

    try {
        // generate decryption key
        const key = await generateKey(ENCRYPTION_KEY_MATERIAL)

        const decrypted = await aesDecrypt(encryptedPassword, key)
        if (!decrypted) return null

        // extract password (remove timestamp)
        const parts = decrypted.split('_')
        return parts[0] // return original password
    } catch (error) {
        ErrorTrackingAPI.sendError(error, 'OTPEncryption', 'decryptPassword')
        return null
    }
}

const otpEncryption = {
    encryptOTP,
    decryptOTP,
    verifyOTP,
    isOTPExpired,
    clearEncryptedOTP,
    encryptPassword,
    decryptPassword,
}

export default otpEncryption
